<template>
    <VJoyTooltip class="profile-availability__tooltip" :text="tooltipContent" :data-testid="dataTestid">
        <VJoyWrapper class="profile-availability" direction="column" no-margin>
            <VJoyAvailability :label="availabilityLabel" :status="availabilityStatus" :is-partial="availabilityIsPartial" />
            <span v-if="showSubtitle" class="profile-availability__subtitle">{{ availabilitySubtitle }}</span>
            <LazyAvailabilityOverviewMask v-if="isInOverviewMode" :data-testid="`${dataTestid}-overview-mask`" />
        </VJoyWrapper>
    </VJoyTooltip>
</template>

<script setup lang="ts">
    import {useRoute} from '@typed-router';
    import {storeToRefs} from 'pinia';
    import {LazyAvailabilityOverviewMask} from '#components';
    import {computed, useTranslation} from '#imports';
    import {VJoyAvailability, VJoyTooltip, VJoyWrapper} from '@maltjoy/core-vue';
    import {useProfileStore} from '~/store/profileStore';
    import type {TranslationKey} from '#build/i18n';

    const {t} = useTranslation();
    const {profile} = storeToRefs(useProfileStore());

    withDefaults(
        defineProps<{
            showSubtitle?: boolean;
            dataTestid?: string;
        }>(),
        {
            showSubtitle: true,
            dataTestid: 'profile-availability-tooltip',
        },
    );

    const route = useRoute();
    const isInOverviewMode = computed<boolean>(() => route?.fullPath?.includes('overview') || false);

    const availabilityLabel = computed(() => {
        return profile.value?.availability.details
            ? t('profile.availability.indicator.explicit.label.v2', {availability: profile.value.availability.details.availability})
            : t('profile.availability.indicator.label.default');
    });
    const availabilityIsPartial = computed(() => profile.value?.availability.details.label === 'PARTIAL');
    const frequencyText = computed(() => {
        return profile.value?.availability.details.frequency ? t(`profile.frequency.text.${profile.value.availability.details.frequency}`) : null;
    });
    const availabilitySubtitle = computed(() => {
        if (profile.value?.availability.details.nextAvailabilityDate) {
            return t(`profile.availability.indicator.next.availability.${profile.value.availability.details.availability}` as TranslationKey, {
                0: new Date(profile.value.availability.details.nextAvailabilityDate).toLocaleDateString(),
            });
        }

        if (availabilityIsPartial.value) {
            return `${t('profile.availability.partial.label')}, ${frequencyText.value}`;
        }

        return '';
    });

    type AvailabilityStatus = 'AVAILABLE' | 'AVAILABLE_AND_VERIFIED' | 'NOT_AVAILABLE_WITH_DATE' | 'AVAILABLE_SOON' | 'NOT_AVAILABLE';
    const availabilityStatus = computed(() => profile.value!.availability.details.availability as AvailabilityStatus);

    const tooltipContent = computed(() => {
        const availability = profile.value!.availability.details.availability;
        const hasNextAvailabilityDate = profile.value?.availability.details.nextAvailabilityDate;

        const workAvailability = profile.value!.availability.details.frequency === 'FULL_TIME' ? 'FULL_TIME' : 'PARTIAL';

        if (hasNextAvailabilityDate) {
            return t('profile.availability.indicator.newtooltip', {
                availability,
                workAvailability,
                nextAvailabilityDate: new Date(profile.value!.availability.details.nextAvailabilityDate).toLocaleDateString(),
                frequency: frequencyText.value,
            });
        }

        if (availability !== 'NOT_AVAILABLE_WITH_DATE') {
            return t('profile.availability.indicator.newtooltip', {
                availability,
                workAvailability,
                frequency: frequencyText.value,
                nextAvailabilityDate: undefined,
            });
        }

        return '';
    });
</script>

<style lang="scss" scoped>
    .profile-availability {
        position: relative;
        gap: var(--joy-core-spacing-1);

        &__tooltip {
            :deep(.joy-tooltip__wrapper) {
                max-width: 100%;
            }
        }

        &__subtitle {
            font-size: var(--joy-font-size-primary-200);
        }
    }
</style>
